<template>
  <section :class="$options.name">
    <div :class="`${$options.name}__button`">
      <EdroneButtonIcon
        :disabled="!state.can('OPEN')"
        :tooltip="trans._notifications.title"
        class="tw:relative tw:!text-2xl"
        type="plain"
        unique-id="notifications-menu__bell-button"
        @click="send('OPEN')"
      >
        <font-awesome-icon :class="!state.can('OPEN') ? 'tw:text-default-300' : ''" icon="fa-light fa-bell" />

        <EdroneBadge
          v-if="state.context.newNotifications || state.context.newActions"
          :disabled="!state.can('OPEN')"
          :label="(state.context.newActions + state.context.newNotifications).toString()"
          :type="state.context.newActions ? 'orange' : 'info'"
          class="tw:absolute"
          size="small"
          solid
          style="top: -4px; right: -4px"
        />
      </EdroneButtonIcon>
    </div>

    <Transition
      appear
      enter-active-class="tw:transition-transform tw:duration-300 tw:ease-out"
      enter-from-class="tw:translate-x-[600px]"
      leave-active-class="tw:transition-transform tw:duration-300 tw:ease-out"
      leave-to-class="tw:translate-x-[600px]"
      name="slide"
    >
      <div
        v-if="state.hasTag('open')"
        :class="[
          `${$options.name}__container`,
          'tw:w-full tw:h-full tw:flex tw:justify-end tw:fixed tw:top-0 tw:left-0 tw:z-[40000] notifications-menu-backdrop',
        ]"
        @click="send('CLOSE')"
      >
        <div
          :class="[
            `${$options.name}__content`,
            'tw:bg-default-000 tw:flex tw:flex-col tw:overflow-hidden tw:border-l tw:border-default-200 tw:w-full tw:max-w-[560px]',
            !isMobile ? 'tw:mt-14' : 'tw:h-full',
          ]"
          style="box-shadow: 0.25rem 1.5rem 1.5rem 0 rgba(0, 0, 0, 0.22)"
          @touchend="handleTouchEnd"
          @touchmove="handleTouchMove"
          @touchstart="handleTouchStart"
          @click.stop
        >
          <div class="tw:pt-3 tw:px-6 tw:border-b tw:border-default-200">
            <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-2 tw:sm:items-center tw:mb-3">
              <h2 class="tw:heading-2">
                {{ trans._notifications.title }}
              </h2>
              <EdroneButton
                :disabled="!(state.context.currentTab === 'notifications' && state.context.newNotifications)"
                class="tw:sm:ms-auto"
                size="small"
                type="outline"
                unique-id="notifications-menu__mark-all-as-read-button"
                @click="send('MARK_AS_READ')"
              >
                <template #left-icon>
                  <font-awesome-icon icon="fa-light fa-envelope-open" />
                </template>
                {{ trans._notifications.buttons.markAllAsRead }}
              </EdroneButton>
            </div>
            <EdroneTabGroup type="underline">
              <EdroneTab
                :active="state.context.currentTab === 'actions'"
                :disabled="state.hasTag('fetchingData')"
                type="underline"
                unique-id="notifications_menu-tab-actions"
                @click="send({ type: 'SWITCH_TAB', tab: 'actions' })"
              >
                {{ trans._notifications.tabs.actions }}
                <template v-if="state.context.newActions" #badge>
                  <EdroneBadge
                    :label="state.context.newActions.toString()"
                    emphasis="high"
                    size="small"
                    type="orange"
                  />
                </template>
              </EdroneTab>
              <EdroneTab
                :active="state.context.currentTab === 'notifications'"
                :disabled="state.hasTag('fetchingData')"
                type="underline"
                unique-id="notifications_menu-tab-notifications-info"
                @click="send({ type: 'SWITCH_TAB', tab: 'notifications' })"
              >
                {{ trans._notifications.tabs.info }}
                <template v-if="state.context.newNotifications" #badge>
                  <EdroneBadge
                    :label="state.context.newNotifications.toString()"
                    emphasis="high"
                    size="small"
                    type="info"
                  />
                </template>
              </EdroneTab>
            </EdroneTabGroup>
          </div>

          <div class="tw:bg-white tw:flex-1 tw:flex tw:flex-col tw:flex-nowrap tw:justify-center tw:overflow-y-auto">
            <div v-if="state.hasTag('fetchingData')" class="tw:p-8">
              <EdroneLoading />
            </div>

            <EdroneResultState
              v-else-if="state.context.errors.includes(state.context.currentTab)"
              class="tw:p-10 tw:mx-10"
              type="caution"
            >
              <h3 class="tw:heading-2 tw:mb-1">
                {{ trans._notifications.states.error.title }}
              </h3>
              <p class="tw:body-2 tw:!text-default-700 tw:mb-4">
                {{ trans._notifications.states.error.message }}
              </p>
              <EdroneButton
                type="outline"
                unique-id="notifications-menu__retry-fetch-notifications-button"
                @click="send('FETCH_NOTIFICATIONS')"
              >
                <template #left-icon>
                  <font-awesome-icon icon="fa-light fa-redo" />
                </template>
                {{ trans._notifications.buttons.retry }}
              </EdroneButton>
            </EdroneResultState>

            <template v-else-if="state.context.currentTab === 'actions'">
              <EdroneResultState
                v-if="!state.context.actions || !state.context.actions.length"
                class="tw:p-10 tw:mx-10"
                type="empty"
              >
                <template #top-icon>
                  <font-awesome-icon icon="fa-light fa-face-sleeping" />
                </template>
                <h3 class="tw:heading-2 tw:mb-1">
                  {{ trans._notifications.states.empty.actions.title }}
                </h3>
                <p class="tw:body-2 tw:!text-default-700">
                  {{ trans._notifications.states.empty.actions.message }}
                </p>
              </EdroneResultState>

              <div v-else class="tw:h-full">
                <ReadyFlowsInsight v-if="isReadyFlowsInsightNotification" />
                <EdroneAlert
                  v-for="(action, index) in notificationsWithoutReadyFlowsInsight"
                  :key="index"
                  :type="getNotificationType(action.notificationType)"
                  class="tw:border-0 tw:border-b tw:border-default-200 tw:rounded-none tw:p-4"
                  is-vertical
                >
                  <template #custom-content>
                    <div class="tw:flex tw:flex-col">
                      <h2 class="tw:body-2--bold tw:mb-1">
                        {{
                          trans._notifications.types[action.notificationType]?.title ||
                          trans._notifications.types.default.title
                        }}
                      </h2>
                      <p class="tw:body-2 tw:mb-3 tw:!text-default-700">
                        {{
                          replaceTags(
                            trans._notifications.types[action.notificationType]?.message ||
                              trans._notifications.types.default.message,
                            action.details
                          )
                        }}
                      </p>
                      <EdroneAlert
                        v-if="trans._notifications.tips[action.notificationType]"
                        :desc="trans._notifications.tips[action.notificationType].message"
                        :title="trans._notifications.tips[action.notificationType].title"
                        class="tw:mb-4"
                        type="tip"
                      />
                      <div class="tw:flex tw:gap-2">
                        <EdroneButton
                          :unique-id="`notifications-menu__action-button-${action.notificationType}-${index}`"
                          size="medium"
                          @click="
                            send({
                              type: 'REDIRECT_FOR_ACTION',
                              action: action.notificationType,
                              details: action.details,
                            })
                          "
                        >
                          <template #right-icon>
                            <font-awesome-icon icon="fa-light fa-arrow-up-right" />
                          </template>
                          {{
                            trans._notifications.types[action.notificationType]?.button ||
                            trans._notifications.types.default.button
                          }}
                        </EdroneButton>
                      </div>
                    </div>
                  </template>
                </EdroneAlert>
              </div>
            </template>
            <template v-else-if="state.context.currentTab === 'notifications'">
              <EdroneResultState
                v-if="!state.context.notifications || !state.context.notifications.length"
                class="tw:p-10 tw:mx-10"
                type="empty"
              >
                <template #top-icon>
                  <font-awesome-icon icon="fa-light fa-mailbox" />
                </template>
                <h3 class="tw:heading-2 tw:mb-1">
                  {{ trans._notifications.states.empty.info.title }}
                </h3>
                <p class="tw:body-2 tw:!text-default-700">
                  {{ trans._notifications.states.empty.info.message }}
                </p>
              </EdroneResultState>

              <div v-else class="tw:h-full">
                <EdroneAlert
                  v-for="(notification, index) in state.context.notifications"
                  :key="index"
                  :class="notification.read ? 'tw:bg-white' : 'tw:bg-default-100'"
                  :type="getNotificationType(notification.notificationType)"
                  class="tw:border-0 tw:border-b tw:border-default-200 tw:rounded-none tw:p-4"
                  is-vertical
                >
                  <template #custom-content>
                    <div class="tw:flex tw:flex-col">
                      <h2 class="tw:body-2--bold tw:mb-1">
                        <CommonDateDisplay
                          :key="notification.creationDate || index + 1"
                          :date="notification.creationDate"
                          class="tw:caption tw:!text-default-700 float-end tw:ms-3"
                          style="white-space: nowrap"
                        />
                        {{
                          trans._notifications.types[notification.notificationType]?.title ||
                          trans._notifications.types.default.title
                        }}
                      </h2>
                      <p class="tw:body-2 tw:mb-3 tw:!text-default-700">
                        {{
                          trans._notifications.types[notification.notificationType]?.message ||
                          trans._notifications.types.default.message
                        }}
                      </p>
                      <div class="tw:flex tw:gap-2">
                        <EdroneButton
                          :type="notification.notificationType === 'BILLING_PAYMENT_TIER_CHANGED' ? 'outline' : 'solid'"
                          :unique-id="`notifications-menu__action-button-${notification.notificationType}-${notification.infoNotificationId}`"
                          size="medium"
                          @click="
                            send({
                              type: 'REDIRECT_FOR_ACTION',
                              action: notification.notificationType,
                              details: notification.details,
                            })
                          "
                        >
                          <template #left-icon>
                            <font-awesome-icon
                              v-if="notification.notificationType === 'BILLING_PAYMENT_TIER_CHANGED'"
                              icon="fa-light fa-wallet"
                            />
                            <font-awesome-icon v-else icon="fa-light fa-message-lines" />
                          </template>
                          {{
                            trans._notifications.types[notification.notificationType]?.button ||
                            trans._notifications.types.default.button
                          }}
                        </EdroneButton>
                      </div>
                    </div>
                  </template>
                </EdroneAlert>
              </div>
            </template>
          </div>
          <EdroneNavigationBottom>
            <template #start>
              <EdroneButton type="text" unique-id="notifications-menu__close-button" @click="send('CLOSE')">
                {{ trans["Close"] }}
              </EdroneButton>
            </template>
          </EdroneNavigationBottom>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { provideNotificationsMenuActor } from "vue-app/components/NotificationsMenu/machines/machineNotificationsMenu";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneTabGroup from "edrone-components/EdroneTabGroup.vue";
import EdroneTab from "edrone-components/EdroneTab.vue";
import EdroneAlert from "edrone-components/EdroneAlert.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneResultState from "edrone-components/EdroneResultState.vue";
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import EdroneNavigationBottom from "edrone-components/EdroneNavigationBottom.vue";
import CommonDateDisplay from "vue-app/components/common/CommonDateDisplay.vue";
import { DTOActionRequiredNotificationType, DTOInfoNotificationType } from "vue-app/api/dto";
import useDeviceResolutionDetector from "vue-app/composables/useDeviceResolutionDetector";
import ReadyFlowsInsight from "../ReadyFlows/ReadyFlowsInsight/ReadyFlowsInsight.vue";

export default defineComponent({
  name: "NotificationsMenu",
  components: {
    CommonDateDisplay,
    EdroneLoading,
    EdroneResultState,
    EdroneAlert,
    EdroneTab,
    EdroneTabGroup,
    EdroneBadge,
    EdroneButtonIcon,
    EdroneButton,
    EdroneNavigationBottom,
    ReadyFlowsInsight,
  },
  setup() {
    const { state, send } = provideNotificationsMenuActor();
    const { isMobile } = useDeviceResolutionDetector(true);

    const touchStartX = ref(0);
    const touchStartTime = ref(0);

    function getNotificationType(notificationType: DTOActionRequiredNotificationType | DTOInfoNotificationType) {
      switch (notificationType) {
        case "FEED_NO_VALID_FEED_CONFIGURATION":
        case "STRIPE_UNPAID_INVOICE_MEDIUM":
        case "STRIPE_UNPAID_INVOICE_HIGH":
        case "NO_WHATSAPP_CREDITS":
        case "NO_SMS_CREDITS":
          return "error";
        case "STRIPE_UNPAID_INVOICE_LOW":
        case "STORE_NOT_INTEGRATED":
        case "STRIPE_NO_VALID_CARD_CONNECTED":
        case "ENGAGEMENT_PAUSED_NO_COUPON_AVAILABLE":
        case "AGENT_EMAIL_NOT_VERIFIED":
        case "SMS_CREDITS_LOW":
        case "WHATSAPP_CREDITS_LOW":
          return "caution";
        case "PAYMENTS_PROVIDER_NOT_FOUND":
        case "STRIPE_NO_PAYMENT_METHOD":
          return "tip";
        default:
          return "info";
      }
    }

    function replaceTags(message: string, details?: { [index: string]: unknown }) {
      if (details) {
        return (
          message
            // @ts-ignore
            .replace("{count}", details["blocked_messages"])
            .replace(
              "{revenue}",
              (Number(details["blocked_messages"]) * Number(details["revenue_from_one_credit"])).toFixed(2) +
                " " +
                window.EDRONE_CONTEXT.user.currency
            )
        );
      }
      return message;
    }

    function handleTouchStart(event: TouchEvent) {
      touchStartX.value = event.touches[0].clientX;
      touchStartTime.value = Date.now();
    }

    function handleTouchMove(event: TouchEvent) {
      const currentX = event.touches[0].clientX;
      const deltaX = currentX - touchStartX.value;

      if (deltaX > 0) {
        event.preventDefault();
      }
    }

    const notificationsWithoutReadyFlowsInsight = computed(() => {
      return state.value.context.actions?.filter(
        (notification) =>
          notification.notificationType !==
          ("AI_REPORT_CREATED" as DTOActionRequiredNotificationType | DTOInfoNotificationType)
      );
    });

    const isReadyFlowsInsightNotification = computed(() => {
      return state.value.context.actions?.some(
        (notification) =>
          notification.notificationType ===
          ("AI_REPORT_CREATED" as DTOActionRequiredNotificationType | DTOInfoNotificationType)
      );
    });

    function handleTouchEnd(event: TouchEvent) {
      const touchEndX = event.changedTouches[0].clientX;
      const deltaX = touchEndX - touchStartX.value;
      const deltaTime = Date.now() - touchStartTime.value;

      const isSwipeRight = deltaX > 50;
      const isQuickSwipe = deltaTime < 300 && deltaX > 30;

      if (isSwipeRight || isQuickSwipe) {
        send("CLOSE");
      }
    }

    return {
      replaceTags,
      state,
      send,
      getNotificationType,
      isMobile,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      notificationsWithoutReadyFlowsInsight,
      isReadyFlowsInsightNotification,
    };
  },
});
</script>

<style scoped>
@media (max-width: 62rem) {
  .notifications-menu-backdrop {
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
}
</style>
